import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Card, Separator, Typography } from '@heroui/react';
import { countParam, renderNotice } from '@/pages/catalogImport/notices';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { ImportSubmitState } from '../../importSubmit';
import type { SuggestedFixesSummary } from '../../suggestedFixes';
import { SuggestedFixesSubmitFailure } from './SuggestedFixesSubmitFailure';
import { SuggestedFixLine } from './SuggestedFixLine';

type SuggestedFixesSummaryBodyProps = {
    summary: SuggestedFixesSummary;
    /** Why the confirmed import did not go through, under the summary. */
    submit: ImportSubmitState;
    /** Says that the whole import can be undone on Result (the wizard). */
    showUndoNote: boolean;
};

/**
 * The dialog's summary: the rows imported, "What we changed" (one
 * `SuggestedFixLine` per kind, in a secondary `Card`, between `Separator`s),
 * the drafts, the rows left out, the header problems that refuse the
 * import, the undo note, and why a confirmed import did not go through.
 */
export const SuggestedFixesSummaryBody: React.FC<
    SuggestedFixesSummaryBodyProps
> = ({ summary, submit, showUndoNote }) => {
    const { t } = useTranslation('pages');
    return (
        <>
            <Typography.Paragraph
                color="muted"
                size="sm"
                {...getNodeTestIdProp('catalog-import-suggested-fixes-intro')}
            >
                {t('catalogImport.products.suggestedFixes.dialog.intro', {
                    count: summary.rows,
                    rows: countParam(summary.rows),
                })}
            </Typography.Paragraph>
            {summary.fixes.length > 0 ? (
                <div className="flex flex-col gap-2">
                    <Typography type="body-sm" weight="medium">
                        {t(
                            'catalogImport.products.suggestedFixes.dialog.fixesTitle',
                        )}
                    </Typography>
                    <Card variant="secondary">
                        <ul
                            className="flex flex-col gap-3"
                            {...getNodeTestIdProp(
                                'catalog-import-suggested-fixes-list',
                            )}
                        >
                            {summary.fixes.map((fix, index) => (
                                <li
                                    className="flex flex-col gap-3"
                                    key={fix.kind}
                                    {...getNodeTestIdProp(
                                        `catalog-import-suggested-fixes-${fix.kind}`,
                                    )}
                                >
                                    {index > 0 ? <Separator /> : null}
                                    <SuggestedFixLine {...fix} />
                                </li>
                            ))}
                        </ul>
                    </Card>
                </div>
            ) : null}
            <div
                className="flex flex-col gap-0.5"
                {...getNodeTestIdProp('catalog-import-suggested-fixes-drafts')}
            >
                <Typography type="body-sm" weight="medium">
                    {summary.drafts > 0
                        ? t(
                              'catalogImport.products.suggestedFixes.dialog.drafts',
                              {
                                  count: summary.drafts,
                                  rows: countParam(summary.drafts),
                              },
                          )
                        : t(
                              'catalogImport.products.suggestedFixes.dialog.noDrafts',
                          )}
                </Typography>
                {summary.drafts > 0 ? (
                    <Typography color="muted" type="body-xs">
                        {t(
                            'catalogImport.products.suggestedFixes.dialog.draftsHow',
                        )}
                    </Typography>
                ) : null}
            </div>
            {summary.leftOut > 0 ? (
                <div
                    className="flex flex-col gap-0.5"
                    {...getNodeTestIdProp(
                        'catalog-import-suggested-fixes-left-out',
                    )}
                >
                    <Typography type="body-sm" weight="medium">
                        {t(
                            'catalogImport.products.suggestedFixes.dialog.leftOut',
                            {
                                count: summary.leftOut,
                                rows: countParam(summary.leftOut),
                            },
                        )}
                    </Typography>
                    <Typography color="muted" type="body-xs">
                        {t(
                            'catalogImport.products.suggestedFixes.dialog.leftOutHow',
                        )}
                    </Typography>
                </div>
            ) : null}
            {summary.refusals.length > 0 ? (
                <Alert
                    status="danger"
                    {...getNodeTestIdProp(
                        'catalog-import-suggested-fixes-refused',
                    )}
                >
                    <Alert.Indicator />
                    <Alert.Content>
                        <Alert.Title>
                            {t(
                                'catalogImport.products.suggestedFixes.dialog.refusedTitle',
                            )}
                        </Alert.Title>
                        <Alert.Description>
                            {t(
                                'catalogImport.products.suggestedFixes.dialog.refused',
                            )}
                            <ul className="mt-1 flex flex-col gap-1">
                                {summary.refusals.map((notice) => (
                                    <li key={notice.key}>
                                        {renderNotice(t, notice)}
                                    </li>
                                ))}
                            </ul>
                        </Alert.Description>
                    </Alert.Content>
                </Alert>
            ) : null}
            {showUndoNote ? (
                <Typography
                    color="muted"
                    type="body-xs"
                    {...getNodeTestIdProp(
                        'catalog-import-suggested-fixes-undo-note',
                    )}
                >
                    {t('catalogImport.products.suggestedFixes.dialog.undoNote')}
                </Typography>
            ) : null}
            <SuggestedFixesSubmitFailure submit={submit} />
        </>
    );
};
